import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  BookmarkIcon,
  ClockIcon,
  SparklesIcon,
  NoSymbolIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import { apiFetch, getSafeHostnames } from '@/utils/api'
import toast from 'react-hot-toast'
import { useAppStore } from '@/store/appStore'
import CyberSecMark from '@/components/icons/CyberSecMark'

type SafeTarget = {
  name: string
  hostname: string
  description: string
  focus: 'SQLi' | 'OWASP Top 10' | 'Mixed'
  docsUrl?: string
  optional?: boolean
  notes?: string
}

type RecentTarget = {
  hostname: string
  addedAt: string
}

type SafeHostConfig = {
  builtin: string[]
  additional: string[]
  all: string[]
}

const normalizeHostArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map(item => String(item || '').trim())
    .filter(Boolean)
}

const SAFE_TARGETS: SafeTarget[] = [
  {
    name: 'Acunetix Test (Vulnweb)',
    hostname: 'testphp.vulnweb.com',
    description: 'Classic DVWA-style target maintained for safe SQL injection training.',
    focus: 'SQLi',
    docsUrl: 'https://vulnweb.com',
    notes: 'Recommended starting point for walkthrough demos.',
  },
  {
    name: 'OWASP Juice Shop',
    hostname: 'juice-shop.herokuapp.com',
    description: 'Purposefully vulnerable single-page app maintained by OWASP.',
    focus: 'OWASP Top 10',
    docsUrl: 'https://owasp.org/www-project-juice-shop/',
    notes: 'Requires patience with rate limits; keep scans polite.',
  },
  {
    name: 'Hackazon (Demo Testfire)',
    hostname: 'demo.testfire.net',
    description: 'Deprecated e-commerce app by IBM; still reliable for SQLi demos.',
    focus: 'SQLi',
    docsUrl: 'https://github.com/rapid7/hackazon',
  },
  {
    name: 'Zero WebApp Security',
    hostname: 'zero.webappsecurity.com',
    description: 'Banking-style application with multiple injection surfaces.',
    focus: 'Mixed',
    docsUrl: 'https://github.com/snoopysecurity/ZeroTest',
  },
  {
    name: 'WebScanTest Playground',
    hostname: 'www.webscantest.com',
    description: 'Multiple interactive labs for injection and auth flaws.',
    focus: 'Mixed',
    docsUrl: 'https://www.webscantest.com',
  },
  {
    name: 'Alt Vulnweb Mirror',
    hostname: 'testasp.vulnweb.com',
    description: 'ASP variant of Vulnweb with similar injection payloads.',
    focus: 'SQLi',
    optional: true,
    docsUrl: 'https://vulnweb.com',
  },
  {
    name: 'bWAPP Docker Demo',
    hostname: 'bwapp.honeybot.io',
    description: 'Hosted Bee-Box instance—verify availability before live demos.',
    focus: 'OWASP Top 10',
    optional: true,
    docsUrl: 'https://github.com/raesene/bwapp-docker',
    notes: 'Community-hosted; mirrors may rotate.',
  },
]

type TargetRecord = {
  id: string
  user_id: string
  org_id?: string | null
  hostname: string
  method: 'http-file' | 'dns-txt'
  token: string
  verified_at?: string | null
  created_at: string
}

export default function Targets() {
  const currentUser = useAppStore(s => s.currentUser)
  const scanPolicy = useAppStore(s => s.scanPolicy)
  const [items, setItems] = useState<TargetRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [hostname, setHostname] = useState('')
  const [method, setMethod] = useState<'http-file' | 'dns-txt'>('http-file')
  const [showOptionalMirrors, setShowOptionalMirrors] = useState(false)
  const [recentTargets, setRecentTargets] = useState<RecentTarget[]>([])
  const [safeHostConfig, setSafeHostConfig] = useState<SafeHostConfig>({ builtin: [], additional: [], all: [] })
  const [safeHostLoading, setSafeHostLoading] = useState(true)
  const [safeHostError, setSafeHostError] = useState<string | null>(null)
  const isAdmin = currentUser?.role === 'admin'
  const targetPolicyNotices: Array<{ key: string; title: string; body: string; tone: 'warning' | 'info'; Icon: React.ComponentType<{ className?: string }> }> = []
  if (!isAdmin && scanPolicy) {
    if (scanPolicy.allowArbitraryVerification === false) {
      targetPolicyNotices.push({
        key: 'verification-locked',
        title: 'Verification requests are limited to curated hosts',
        body: 'Choose a domain from the safe-target list below or ask an administrator to add your lab environment. Custom domains are paused for preview testers.',
        tone: 'warning',
        Icon: NoSymbolIcon,
      })
    }
    if (scanPolicy.enforcePreapprovedTargets) {
      targetPolicyNotices.push({
        key: 'preapproved-required',
        title: 'Only preapproved targets can be scanned right now',
        body: 'Even verified domains must match the curated safe-host inventory before a scan will start. Use the provided demo environments or coordinate with an administrator.',
        tone: 'info',
        Icon: ShieldCheckIcon,
      })
    }
    if (scanPolicy.disableNonAdminScans) {
      targetPolicyNotices.push({
        key: 'scans-paused',
        title: 'Researcher scans are temporarily paused',
        body: 'You can still prepare verification tokens so they are ready once access resumes.',
        tone: 'info',
        Icon: ShieldCheckIcon,
      })
    }
  }

  const historyKey = useMemo(
    () => `targetHistory:${currentUser?.id ?? 'anonymous'}`,
    [currentUser?.id]
  )

  const setConfigFromResponse = useCallback((config: Partial<SafeHostConfig> | null | undefined) => {
    const builtin = normalizeHostArray(config?.builtin)
    const additional = normalizeHostArray(config?.additional)
    const providedAll = normalizeHostArray(config?.all)
    const mergedAll = providedAll.length > 0
      ? providedAll
      : Array.from(new Set([...builtin, ...additional]))

    setSafeHostConfig({ builtin, additional, all: mergedAll })
  }, [])

  const curatedTargets = useMemo(() => {
    const base = SAFE_TARGETS.filter(target => showOptionalMirrors || !target.optional)
    if (safeHostConfig.all.length === 0) return base
    const allowed = new Set(safeHostConfig.all.map(host => host.toLowerCase()))
    return base.filter(target => allowed.has(target.hostname.toLowerCase()))
  }, [showOptionalMirrors, safeHostConfig.all])

  const additionalSafeHosts = useMemo(() => {
    if (safeHostConfig.all.length === 0) return []
    const curatedHostnames = new Set(SAFE_TARGETS.map(target => target.hostname.toLowerCase()))
    return safeHostConfig.all.filter(host => !curatedHostnames.has(host.toLowerCase()))
  }, [safeHostConfig.all])

  const additionalHostSet = useMemo(
    () => new Set(safeHostConfig.additional.map(host => host.toLowerCase())),
    [safeHostConfig.additional]
  )

  const refreshSafeHosts = useCallback(async () => {
    setSafeHostLoading(true)
    try {
      const config = await getSafeHostnames()
      setConfigFromResponse(config)
      setSafeHostError(null)
    } catch (error: any) {
      setSafeHostError(error?.message || 'Unable to fetch safe host list')
    } finally {
      setSafeHostLoading(false)
    }
  }, [setConfigFromResponse])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setSafeHostLoading(true)
      try {
        const config = await getSafeHostnames()
        if (cancelled) return
        setConfigFromResponse(config)
        setSafeHostError(null)
      } catch (error: any) {
        if (cancelled) return
        setSafeHostError(error?.message || 'Unable to fetch safe host list')
      } finally {
        if (!cancelled) {
          setSafeHostLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [setConfigFromResponse])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const stored = window.localStorage.getItem(historyKey)
      if (stored) {
        const parsed = JSON.parse(stored) as RecentTarget[]
        setRecentTargets(parsed)
      } else {
        setRecentTargets([])
      }
    } catch (error) {
      console.warn('Unable to load target history', error)
      setRecentTargets([])
    }
  }, [historyKey])

  const saveHistory = (targetHostname: string) => {
    if (typeof window === 'undefined' || !targetHostname) return
    setRecentTargets(prev => {
      const existing = prev.filter(entry => entry.hostname !== targetHostname)
      const next = [
        { hostname: targetHostname, addedAt: new Date().toISOString() },
        ...existing,
      ].slice(0, 5)
      try {
        window.localStorage.setItem(historyKey, JSON.stringify(next))
      } catch (error) {
        console.warn('Unable to persist target history', error)
      }
      return next
    })
  }

  const clearHistory = () => {
    if (typeof window === 'undefined') return
    window.localStorage.removeItem(historyKey)
    setRecentTargets([])
  }

  const handleSelectSafeTarget = (targetHostname: string) => {
    setHostname(targetHostname)
    setMethod('http-file')
    saveHistory(targetHostname)
    toast.success('Hostname populated from safe host list')
  }

  const load = async () => {
    setLoading(true)
    try {
      const data = await apiFetch<TargetRecord[]>('/api/targets')
      setItems(data)
    } catch (e: any) {
      toast.error(e.message || 'Failed to load targets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hostname) return
    setCreating(true)
    try {
      await apiFetch<{ id: string; hostname: string; method: string; token: string }>(
        '/api/targets',
        { method: 'POST', body: JSON.stringify({ hostname, method }) }
      )
      toast.success('Verification request created')
      saveHistory(hostname)
      setHostname('')
      await load()
    } catch (e: any) {
      toast.error(e.message || 'Failed to create request')
    } finally {
      setCreating(false)
    }
  }

  const onVerify = async (id: string) => {
    try {
      await apiFetch(`/api/targets/${id}/verify`, { method: 'POST' })
      toast.success('Target verified')
      await load()
    } catch (e: any) {
      toast.error(e.message || 'Verification failed')
    }
  }

  const onDelete = async (id: string) => {
    try {
      await apiFetch(`/api/targets/${id}`, { method: 'DELETE' })
      toast('Removed')
      setItems(prev => prev.filter(i => i.id !== id))
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete')
    }
  }

  const instructions = useMemo(() => {
    if (!hostname) return null
    if (method === 'http-file') {
      return (
        <div className="text-sm text-gray-300">
          <p className="mb-1">Create the following file on your site:</p>
          <code className="bg-gray-800 px-2 py-1 rounded">/.well-known/cybersec-verify.txt</code>
          <p className="mt-2">Then click Verify. The file must contain the token we generated for this hostname.</p>
        </div>
      )
    }
    return (
      <div className="text-sm text-gray-300">
        <p>Add a DNS TXT record for your domain containing the provided token, then click Verify.</p>
      </div>
    )
  }, [hostname, method])

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center">
            <CyberSecMark className="h-8 w-8 mr-3 text-blue-400" />
            Verify Target Ownership
          </h1>
          <p className="mt-2 text-gray-400">Verify domains you're allowed to test. Non-admins must verify before scanning.</p>
          {targetPolicyNotices.length > 0 && (
            <div className="mt-4 space-y-3">
              {targetPolicyNotices.map(({ key, title, body, tone, Icon }) => (
                <div
                  key={key}
                  className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
                    tone === 'warning' ? 'border-amber-400/60 bg-amber-900/20' : 'border-blue-500/60 bg-blue-900/20'
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 mt-0.5 ${
                      tone === 'warning' ? 'text-amber-200' : 'text-blue-200'
                    }`}
                    aria-hidden="true"
                  />
                  <div>
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="text-xs text-gray-200 mt-1">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mb-8">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BookmarkIcon className="w-6 h-6 text-blue-400" />
                  Curated Demo Targets
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Safe-to-scan environments for workshops, labs, and academic demonstrations.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowOptionalMirrors(prev => !prev)}
                className="text-xs px-3 py-1.5 rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
              >
                {showOptionalMirrors ? 'Hide optional mirrors' : 'Show optional mirrors'}
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {safeHostLoading && (
                <div className="text-sm text-gray-400 bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  Loading curated targets…
                </div>
              )}
              {!safeHostLoading && !safeHostError && curatedTargets.length === 0 && (
                <div className="text-sm text-gray-400 bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  No curated targets are currently available for your workspace configuration.
                </div>
              )}
              {!safeHostLoading && !safeHostError && curatedTargets.length > 0 && (
                <div className="space-y-4">
                  {curatedTargets.map(target => (
                    <div key={target.hostname} className="border border-gray-700 rounded-lg p-4 bg-gray-900/60">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-white font-medium flex items-center gap-2">
                            <span>{target.name}</span>
                            <span className="text-[0.65rem] uppercase tracking-wide bg-gray-800 text-gray-300 px-2 py-0.5 rounded">
                              {target.focus}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400 mt-1">{target.description}</p>
                          <div className="text-xs text-gray-500 mt-2">
                            Hostname{' '}
                            <code className="bg-gray-800 px-1.5 py-0.5 rounded break-all">{target.hostname}</code>
                          </div>
                          {target.notes && (
                            <p className="text-xs text-gray-500 mt-1">{target.notes}</p>
                          )}
                          {target.docsUrl && (
                            <a
                              href={target.docsUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 mt-2"
                            >
                              Learn more ↗
                            </a>
                          )}
                        </div>
                        <div className="flex w-full sm:w-auto flex-col items-stretch sm:items-end gap-2 shrink-0">
                          {additionalHostSet.has(target.hostname.toLowerCase()) && (
                            <span className="text-[0.65rem] uppercase tracking-wide text-emerald-300 bg-emerald-900/30 px-2 py-0.5 rounded">
                              Workspace Safe Host
                            </span>
                          )}
                          {target.optional && (
                            <span className="text-[0.65rem] uppercase tracking-wide text-amber-300 bg-amber-900/30 px-2 py-0.5 rounded">
                              Optional
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleSelectSafeTarget(target.hostname)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm w-full sm:w-auto"
                          >
                            Use host
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <CyberSecMark className="w-6 h-6 text-emerald-400" />
                  Preapproved Safe Hostnames
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Combined list of built-in demo targets and any workspace overrides provisioned by your admins.
                </p>
              </div>
              <button
                type="button"
                onClick={refreshSafeHosts}
                className="text-xs px-3 py-1.5 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 disabled:opacity-50"
                disabled={safeHostLoading}
              >
                {safeHostLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {safeHostError && (
                <div className="text-sm text-amber-300 bg-amber-900/20 border border-amber-500/40 rounded-lg p-4">
                  {safeHostError}
                </div>
              )}

              {safeHostLoading && (
                <div className="text-sm text-gray-400 bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                  Loading safe host list…
                </div>
              )}

              {!safeHostLoading && !safeHostError && (
                <>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Built-in training catalog</div>
                    {safeHostConfig.builtin.length > 0 ? (
                      <ul className="grid gap-2 sm:grid-cols-2">
                        {safeHostConfig.builtin.map(host => (
                          <li key={`builtin-${host}`} className="flex items-center gap-2 text-sm text-gray-200 bg-gray-900/60 border border-gray-800 rounded px-3 py-2">
                            <CheckCircleIcon className="w-4 h-4 text-blue-400" />
                            <code className="text-gray-100 break-all">{host}</code>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-xs text-gray-500 bg-gray-900/60 border border-gray-800 rounded px-3 py-2">
                        No built-in hosts are currently enabled.
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">Workspace safe hosts</div>
                    {additionalSafeHosts.length > 0 ? (
                      <ul className="grid gap-2 sm:grid-cols-2">
                        {additionalSafeHosts.map(host => (
                          <li key={`workspace-${host}`} className="flex items-center gap-2 text-sm text-gray-200 bg-emerald-900/20 border border-emerald-700/50 rounded px-3 py-2">
                            <SparklesIcon className="w-4 h-4 text-emerald-300" />
                            <code className="text-gray-100 break-all">{host}</code>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-xs text-gray-500 bg-gray-900/60 border border-gray-800 rounded px-3 py-2">
                        No workspace-specific hosts have been configured yet.
                      </div>
                    )}
                  </div>

                  {safeHostConfig.all.length > 0 && (
                    <div className="text-xs text-gray-500">
                      Total safe hosts: {safeHostConfig.all.length}. Workspace overrides appear with the green accent.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ClockIcon className="w-6 h-6 text-blue-400" />
                  Recent Targets
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Last five verification requests tied to your account.
                </p>
              </div>
              <button
                type="button"
                onClick={clearHistory}
                className="text-xs px-3 py-1.5 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 disabled:opacity-40 w-full sm:w-auto"
                disabled={recentTargets.length === 0}
              >
                Clear history
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {recentTargets.length === 0 && (
                <div className="text-sm text-gray-500 bg-gray-900/60 border border-dashed border-gray-700 rounded-lg p-4">
                  No recent targets yet. Create a verification request or choose a curated host to seed history.
                </div>
              )}
              {recentTargets.map(entry => (
                <button
                  key={`${entry.hostname}-${entry.addedAt}`}
                  type="button"
                  onClick={() => {
                    setHostname(entry.hostname)
                    setMethod('http-file')
                    toast.success('Hostname populated from recent history')
                  }}
                  className="w-full text-left bg-gray-900/60 border border-gray-700 hover:border-blue-500 hover:bg-gray-900 rounded-lg px-4 py-3 transition"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-left">
                      <SparklesIcon className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-white font-medium break-words">{entry.hostname}</span>
                    </div>
                    <span className="text-xs text-gray-400 break-words text-left sm:text-right">
                      {new Date(entry.addedAt).toLocaleString()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-8">
          <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Hostname</label>
              <input value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="example.com" className="w-full bg-gray-900 text-gray-100 border border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Method</label>
              <select aria-label="Verification Method" title="Verification Method" value={method} onChange={(e) => setMethod(e.target.value as any)} className="w-full bg-gray-900 text-gray-100 border border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="http-file">HTTP File</option>
                <option value="dns-txt">DNS TXT</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2 sm:flex-nowrap">
              <button disabled={creating} aria-label="Create verification request" className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white disabled:opacity-50 w-full sm:w-auto">
                <PlusIcon className="w-5 h-5 mr-2" /> Create Request
              </button>
              <button type="button" onClick={load} aria-label="Refresh" title="Refresh" className="inline-flex items-center justify-center px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded text-gray-100 w-full sm:w-auto">
                <ArrowPathIcon className="w-5 h-5" /> Refresh
              </button>
            </div>
          </form>
          {instructions && <div className="mt-4">{instructions}</div>}
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-white font-semibold">Your Targets</h2>
            {loading && <span className="text-xs text-gray-400">Loading…</span>}
          </div>
          <div className="divide-y divide-gray-700">
            {items.length === 0 && (
              <div className="p-6 text-gray-400">No targets yet. Create one above.</div>
            )}
            {items.map(item => (
              <div key={item.id} className="p-6 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="text-white font-medium break-words text-balance" title={item.hostname}>{item.hostname}</div>
                  <div className="text-xs text-gray-400 mt-1">Method: {item.method}</div>
                  <div className="text-xs text-gray-400 mt-1 break-words">
                    Token: <code className="bg-gray-900 px-1 py-0.5 rounded break-all">{item.token}</code>
                  </div>
                  <div className="text-xs mt-1">
                    {item.verified_at ? (
                      <span className="text-green-400 inline-flex items-center"><CheckCircleIcon className="w-4 h-4 mr-1" /> Verified {new Date(item.verified_at).toLocaleString()}</span>
                    ) : (
                      <span className="text-yellow-400">Not verified</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-1 sm:flex-none flex-wrap items-center justify-end gap-2 shrink-0">
                  {!item.verified_at && (
                    <button onClick={() => onVerify(item.id)} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded text-white w-full sm:w-auto">Verify</button>
                  )}
                  <button onClick={() => onDelete(item.id)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-100 inline-flex items-center justify-center w-full sm:w-auto">
                    <TrashIcon className="w-4 h-4 mr-1" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
